"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import styles from "./News.module.css"
import { Helmet } from "react-helmet-async"

// Array of months for tab navigation
const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

const News = () => {
  const [events, setEvents] = useState([])
  const [selectedMonth, setSelectedMonth] = useState(months[new Date().getMonth()])
  const [expandedIndex, setExpandedIndex] = useState(-1)
  const [modalImage, setModalImage] = useState(null)
  const [modalTitle, setModalTitle] = useState("")
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [error, setError] = useState(null)
  const [modalZoom, setModalZoom] = useState(1)
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await axios.get("https://hillgreenhighschool.in/api/fetch_news.php")

        if (Array.isArray(response.data)) {
          setEvents(response.data)
        } else {
          console.error("Response is not an array:", response.data)
          setEvents([])
          setError("Invalid data format received")
        }
      } catch (error) {
        console.error("Error fetching news:", error)
        setError("Failed to load news. Please try again later.")
        setEvents([])
      } finally {
        setLoading(false)
      }
    }

    fetchNews()
  }, [])

  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.date)
    const monthName = months[eventDate.getMonth()]
    const matchesMonth = monthName === selectedMonth
    const matchesSearch =
      searchTerm === "" ||
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesMonth && matchesSearch
  })

  const toggleReadMore = (index) => {
    setExpandedIndex(expandedIndex === index ? -1 : index)
  }

  const openModal = (image, title) => {
    setModalImage(image)
    setModalTitle(title)
    setModalZoom(1)
    setModalPosition({ x: 0, y: 0 })
    setIsDragging(false)
    setDragStart({ x: 0, y: 0 })
    setLastPosition({ x: 0, y: 0 })
    document.body.style.overflow = "hidden"
  }

  const closeModal = () => {
    setModalImage(null)
    setModalTitle("")
    setModalZoom(1)
    setModalPosition({ x: 0, y: 0 })
    setIsDragging(false)
    setDragStart({ x: 0, y: 0 })
    setLastPosition({ x: 0, y: 0 })
    document.body.style.overflow = "unset"
  }

  const handleZoomIn = () => {
    setModalZoom((prev) => Math.min(prev + 0.5, 3))
  }

  const handleZoomOut = () => {
    setModalZoom((prev) => Math.max(prev - 0.5, 0.5))
    if (modalZoom <= 1) {
      setModalPosition({ x: 0, y: 0 })
    }
  }

  const handleResetZoom = () => {
    setModalZoom(1)
    setModalPosition({ x: 0, y: 0 })
  }

  const handleMouseDown = (e) => {
    if (modalZoom > 1) {
      setIsDragging(true)
      setDragStart({ x: e.clientX, y: e.clientY })
      setLastPosition(modalPosition)
      e.preventDefault()
    }
  }

  const handleMouseMove = (e) => {
    if (isDragging && modalZoom > 1) {
      const deltaX = e.clientX - dragStart.x
      const deltaY = e.clientY - dragStart.y

      const maxOffset = 100 * modalZoom
      const newX = Math.max(-maxOffset, Math.min(maxOffset, lastPosition.x + deltaX / modalZoom))
      const newY = Math.max(-maxOffset, Math.min(maxOffset, lastPosition.y + deltaY / modalZoom))

      setModalPosition({ x: newX, y: newY })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleTouchStart = (e) => {
    if (modalZoom > 1 && e.touches.length === 1) {
      setIsDragging(true)
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY })
      setLastPosition(modalPosition)
      e.preventDefault()
    }
  }

  const handleTouchMove = (e) => {
    if (isDragging && modalZoom > 1 && e.touches.length === 1) {
      const deltaX = e.touches[0].clientX - dragStart.x
      const deltaY = e.touches[0].clientY - dragStart.y

      const maxOffset = 100 * modalZoom
      const newX = Math.max(-maxOffset, Math.min(maxOffset, lastPosition.x + deltaX / modalZoom))
      const newY = Math.max(-maxOffset, Math.min(maxOffset, lastPosition.y + deltaY / modalZoom))

      setModalPosition({ x: newX, y: newY })
      e.preventDefault()
    }
  }

  const handleTouchEnd = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      document.addEventListener("touchmove", handleTouchMove, { passive: false })
      document.addEventListener("touchend", handleTouchEnd)

      return () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
        document.removeEventListener("touchmove", handleTouchMove)
        document.removeEventListener("touchend", handleTouchEnd)
      }
    }
  }, [isDragging, dragStart, lastPosition, modalZoom])

  const shareEvent = async (event) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text: event.description.substring(0, 100) + "...",
          url: window.location.href,
        })
      } catch (error) {
        console.log("Error sharing:", error)
      }
    } else {
      const shareText = `${event.title}\n${event.description.substring(0, 100)}...\n${window.location.href}`
      navigator.clipboard.writeText(shareText)
      alert("Event details copied to clipboard!")
    }
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (modalImage) {
        switch (event.key) {
          case "Escape":
            closeModal()
            break
          case "=":
          case "+":
            event.preventDefault()
            handleZoomIn()
            break
          case "-":
            event.preventDefault()
            handleZoomOut()
            break
          case "0":
            event.preventDefault()
            handleResetZoom()
            break
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [modalImage])

  const LoadingSkeleton = () => (
    <div className={styles.skeleton_container}>
      {[...Array(3)].map((_, index) => (
        <div key={index} className={styles.skeleton_event}>
          <div className={styles.skeleton_image}></div>
          <div className={styles.skeleton_content}>
            <div className={styles.skeleton_title}></div>
            <div className={styles.skeleton_date}></div>
            <div className={styles.skeleton_description}></div>
            <div className={styles.skeleton_description}></div>
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <>
      <Helmet>
        <title>Hillgreen School News</title>
        <meta name="description" content="Hillgreen School News" />
        <meta name="keywords" content="Hillgreen, School, News, Events" />
        <link rel="canonical" href="https://www.hillgreenhighschool.in/news" />
      </Helmet>

      <div className={styles.news}>
        <div className={styles.news_head}>
          <div className={styles.overlay}></div>
          <div className={styles.news_head_text}>
            <h1>News</h1>
          </div>
        </div>

        <div className={styles.search_container}>
          <input
            type="text"
            placeholder="Search news and events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.search_input}
          />
          <div className={styles.search_icon}>🔍</div>
        </div>

        <div className={styles.news_tabs}>
          {months.map((month) => (
            <button
              key={month}
              className={`${selectedMonth === month ? styles.active : ""} ${month === months[new Date().getMonth()] ? styles.current_month : ""}`}
              onClick={() => setSelectedMonth(month)}
            >
              {month}
              {month === months[new Date().getMonth()] && <span className={styles.current_indicator}>•</span>}
            </button>
          ))}
        </div>

        <div className={styles.news_main}>
          <div className={styles.news_header}>
            <h1>{selectedMonth} Events</h1>
            {filteredEvents.length > 0 && (
              <div className={styles.results_count}>
                {filteredEvents.length} event{filteredEvents.length !== 1 ? "s" : ""} found
              </div>
            )}
          </div>

          {error && (
            <div className={styles.error_message}>
              <p>{error}</p>
              <button onClick={() => window.location.reload()} className={styles.retry_button}>
                Try Again
              </button>
            </div>
          )}

          {loading ? (
            <LoadingSkeleton />
          ) : filteredEvents.length === 0 ? (
            <div className={styles.no_events}>
              <div className={styles.no_events_icon}>📅</div>
              <p>No events found for {selectedMonth}</p>
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} className={styles.clear_search}>
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            filteredEvents.map((event, index) => (
              <div className={styles.event} key={index}>
                <div className={styles.event_image}>
                  <img
                    src={`data:image/jpeg;base64,${event.image}`}
                    alt={event.title}
                    className={styles.newsImage}
                    onClick={() => openModal(`data:image/jpeg;base64,${event.image}`, event.title)}
                    loading="lazy"
                  />
                  <div className={styles.zoom_indicator}>
                    <span>🔍</span>
                  </div>
                </div>
                <div className={styles.event_details}>
                  <div className={styles.event_header}>
                    <h2>{event.title}</h2>
                    <button onClick={() => shareEvent(event)} className={styles.share_button} title="Share this event">
                      📤
                    </button>
                  </div>
                  <p className={styles.event_date}>
                    {new Date(event.date).toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                  <p className={styles.event_description}>
                    {expandedIndex === index ? event.description : `${event.description.substring(0, 200)}...`}
                  </p>
                  {event.description.length > 200 && (
                    <p className={styles.readmore} onClick={() => toggleReadMore(index)}>
                      {expandedIndex === index ? "Read Less" : "Read More"}{" "}
                      <span className={styles.readmore_arrow}>&rarr;</span>
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {modalImage && (
          <div className={styles.modal_overlay} onClick={closeModal}>
            <div className={styles.modal_content} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modal_controls}>
                <button onClick={handleZoomOut} className={styles.zoom_button} title="Zoom Out (-)">
                  🔍-
                </button>
                <button onClick={handleResetZoom} className={styles.zoom_button} title="Reset Zoom (0)">
                  ↻
                </button>
                <button onClick={handleZoomIn} className={styles.zoom_button} title="Zoom In (+)">
                  🔍+
                </button>
                <button className={styles.modal_close} onClick={closeModal} title="Close (Esc)">
                  &times;
                </button>
              </div>
              <div className={styles.modal_image_container}>
                <img
                  src={modalImage || "/placeholder.svg"}
                  alt={modalTitle}
                  className={styles.modal_image}
                  style={{
                    transform: `scale(${modalZoom}) translate(${modalPosition.x}px, ${modalPosition.y}px)`,
                    cursor: modalZoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
                    userSelect: "none",
                    transition: isDragging ? "none" : "transform 0.2s ease",
                  }}
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleTouchStart}
                  draggable={false}
                />
              </div>
              <div className={styles.modal_title}>{modalTitle}</div>
              <div className={styles.modal_instructions}>
                Use +/- keys to zoom, drag to pan when zoomed, 0 to reset, Esc to close
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default News
